/**
 * The question palette: a legend and a grid of numbered buttons, each coloured
 * by its state, following the convention in `answerState.ts`.
 *
 * Colour is never the only signal — every swatch in the legend carries its
 * count and its label, and the active question also gets a ring, so the palette
 * is readable without relying on hue discrimination.
 */
import type { AnswerResponse } from "@exam/shared";
import { cn } from "@/lib/cn";
import {
  PALETTE_LABEL,
  paletteStateFor,
  type PaletteState,
} from "@/lib/exam/answerState";

/** Swatch styles, shared by the legend and the grid so they cannot drift. */
const STATE_STYLES: Record<PaletteState, string> = {
  not_visited: "bg-neutral-soft text-ink-muted border-border-strong",
  not_answered: "bg-violation-soft text-violation border-violation/30",
  answered: "bg-answered-soft text-answered border-answered/30",
  review: "bg-review-soft text-review border-review/40",
  answered_review: "bg-review text-white border-review",
};

const LEGEND_ORDER: PaletteState[] = [
  "answered",
  "not_answered",
  "review",
  "answered_review",
  "not_visited",
];

export interface PaletteItem {
  questionId: string;
  /** 1-based, as shown to the student. */
  number: number;
}

/** One subject's block of the palette. */
export interface PaletteGroup {
  subjectName: string;
  items: PaletteItem[];
}

export function QuestionPalette({
  groups,
  responses,
  markedForReview,
  visited,
  activeQuestionId,
  onSelect,
}: {
  /** One group per subject; a single-subject paper is just one group. */
  groups: PaletteGroup[];
  responses: Map<string, AnswerResponse | null>;
  markedForReview: Set<string>;
  visited: Set<string>;
  activeQuestionId: string | null;
  onSelect: (questionId: string) => void;
}) {
  const stateOf = (questionId: string): PaletteState =>
    paletteStateFor(
      responses.get(questionId),
      markedForReview.has(questionId),
      visited.has(questionId),
    );

  const allItems = groups.flatMap((g) => g.items);
  const tally = LEGEND_ORDER.map((state) => ({
    state,
    count: allItems.filter((item) => stateOf(item.questionId) === state).length,
  }));

  // A single-subject paper needs no per-subject headings.
  const showHeadings = groups.length > 1;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-xs font-semibold text-ink">Question palette</h2>
        <p className="mt-0.5 text-2xs text-ink-faint">
          Select a question to jump to it.
        </p>
      </div>

      <ul className="flex flex-col gap-1.5">
        {tally.map(({ state, count }) => (
          <li key={state} className="flex items-center gap-2">
            <span
              aria-hidden
              className={cn(
                "inline-flex size-4 shrink-0 items-center justify-center rounded-sm border",
                STATE_STYLES[state],
              )}
            />
            <span className="text-2xs text-ink-muted">
              {PALETTE_LABEL[state]}
            </span>
            <span className="tabular ml-auto text-2xs font-medium text-ink">
              {count}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex flex-col gap-3 border-t border-border pt-4">
        {groups.map((group) => (
          <div key={group.subjectName} className="flex flex-col gap-1.5">
            {showHeadings ? (
              <h3 className="text-2xs font-semibold text-ink-muted">
                {group.subjectName}
              </h3>
            ) : null}
            <div
              role="group"
              aria-label={`${group.subjectName} questions`}
              className="grid grid-cols-5 gap-1.5"
            >
              {group.items.map((item) => {
                const state = stateOf(item.questionId);
                const active = item.questionId === activeQuestionId;

                return (
                  <button
                    key={item.questionId}
                    type="button"
                    onClick={() => onSelect(item.questionId)}
                    aria-current={active ? "true" : undefined}
                    aria-label={`Question ${item.number}: ${PALETTE_LABEL[state]}`}
                    title={`Question ${item.number} — ${PALETTE_LABEL[state]}`}
                    className={cn(
                      "tabular flex h-8 items-center justify-center rounded-sm border",
                      "text-2xs font-medium transition-colors",
                      STATE_STYLES[state],
                      active && "outline-2 outline-offset-1 outline-accent",
                    )}
                  >
                    {item.number}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
